import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';
import './RecipeDetail.css';

const difficultyLabel = { easy: 'Fácil', medium: 'Media', hard: 'Difícil' };
const difficultyColor = { easy: '#4caf50', medium: '#ff9800', hard: '#f44336' };

function formatDate(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleDateString('es-ES', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
}

function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [likes, setLikes] = useState(0);
  const [userLiked, setUserLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState('');

  useEffect(() => {
    client.get(`/api/recipes/${id}`)
      .then(res => {
        setRecipe(res.data);
        setLikes(res.data.likes_count || 0);
        setUserLiked(res.data.user_liked || false);
        setComments(res.data.comments || []);
      })
      .catch(() => setError('Receta no encontrada.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta receta?')) return;
    setDeleting(true);
    try {
      await client.delete(`/api/recipes/${id}`);
      navigate('/');
    } catch (err) {
      setError('Error al eliminar la receta.');
      setDeleting(false);
    }
  };

  const handleLike = async () => {
    if (!user || user.status !== 'approved') return;
    setLikeLoading(true);
    try {
      const res = await client.post(`/api/recipes/${id}/like`);
      setLikes(res.data.likes_count);
      setUserLiked(res.data.liked);
    } catch {
      // silently ignore
    } finally {
      setLikeLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setCommentError('');
    setCommentLoading(true);
    try {
      const res = await client.post(`/api/recipes/${id}/comments`, { content: newComment.trim() });
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch (err) {
      setCommentError(err.response?.data?.error || 'Error al publicar el comentario.');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('¿Eliminar este comentario?')) return;
    try {
      await client.delete(`/api/recipes/${id}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {
      // silently ignore
    }
  };

  const canEdit = user && recipe && (user.id === recipe.user_id || user.role === 'admin');
  const canInteract = user && user.status === 'approved';

  if (loading) return <div className="loading">Cargando receta...</div>;
  if (error) return <div className="error-msg" style={{ marginTop: 24 }}>{error}</div>;
  if (!recipe) return null;

  return (
    <div className="recipe-detail">
      {recipe.images && recipe.images.length > 0 && (
        <img
          src={recipe.images[0].url}
          alt={recipe.title}
          className="recipe-detail-img"
        />
      )}

      <div className="recipe-detail-header">
        <h1 className="recipe-detail-title">{recipe.title}</h1>
        <div className="recipe-detail-meta">
          {recipe.prep_time && <span className="meta-tag">⏱ {recipe.prep_time} min</span>}
          {recipe.difficulty && (
            <span
              className="recipe-badge"
              style={{ backgroundColor: difficultyColor[recipe.difficulty] || '#999' }}
            >
              {difficultyLabel[recipe.difficulty] || recipe.difficulty}
            </span>
          )}
        </div>
        {recipe.author && <p className="recipe-detail-author">por {recipe.author}</p>}
        {recipe.created_at && (
          <p className="recipe-detail-date">📅 Publicada el {formatDate(recipe.created_at)}</p>
        )}
      </div>

      {recipe.description && (
        <div className="recipe-detail-section card">
          <p className="recipe-detail-desc">{recipe.description}</p>
        </div>
      )}

      {recipe.ingredients && recipe.ingredients.length > 0 && (
        <div className="recipe-detail-section card">
          <h2 className="section-title">🧂 Ingredientes</h2>
          <ul className="ingredients-list">
            {recipe.ingredients.map(ing => (
              <li key={ing.id} className="ingredient-item">
                <span className="ingredient-name">{ing.name}</span>
                {(ing.quantity || ing.unit) && (
                  <span className="ingredient-qty">
                    {ing.quantity} {ing.unit}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {recipe.steps && recipe.steps.length > 0 && (
        <div className="recipe-detail-section card">
          <h2 className="section-title">👨‍🍳 Pasos</h2>
          <ol className="steps-list">
            {recipe.steps.map(step => (
              <li key={step.id} className="step-item">
                <span className="step-num">{step.order_num}</span>
                <p className="step-desc">{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      {recipe.images && recipe.images.length > 1 && (
        <div className="recipe-detail-section card">
          <h2 className="section-title">📷 Fotos</h2>
          <div className="recipe-images-grid">
            {recipe.images.slice(1).map(img => (
              <img key={img.id} src={img.url} alt={recipe.title} className="recipe-extra-img" />
            ))}
          </div>
        </div>
      )}

      {/* Likes */}
      <div className="recipe-likes">
        <button
          className={`btn like-btn ${userLiked ? 'btn-primary' : 'btn-outline'}`}
          onClick={handleLike}
          disabled={!canInteract || likeLoading}
          title={canInteract ? '' : 'Inicia sesión con una cuenta aprobada para dar me gusta'}
        >
          ❤️ {likes} {likes === 1 ? 'me gusta' : 'me gustan'}
        </button>
      </div>

      {/* Comments */}
      <div className="recipe-detail-section card">
        <h2 className="section-title">💬 Comentarios ({comments.length})</h2>

        {canInteract ? (
          <form onSubmit={handleAddComment} className="comment-form">
            {commentError && <div className="error-msg">{commentError}</div>}
            <textarea
              className="form-control comment-textarea"
              rows={2}
              placeholder="Escribe un comentario..."
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              required
            />
            <button
              type="submit"
              className="btn btn-secondary btn-sm"
              disabled={commentLoading || !newComment.trim()}
            >
              {commentLoading ? 'Publicando…' : 'Publicar'}
            </button>
          </form>
        ) : (
          <p className="comment-login-hint">
            <Link to="/login">Inicia sesión</Link> con una cuenta aprobada para comentar.
          </p>
        )}

        {comments.length === 0 ? (
          <p className="no-comments">Aún no hay comentarios. ¡Sé el primero!</p>
        ) : (
          <ul className="comments-list">
            {comments.map(c => (
              <li key={c.id} className="comment-item">
                <div className="comment-header">
                  <span className="comment-author">{c.author}</span>
                  <span className="comment-date">{formatDate(c.created_at)}</span>
                  {user && (user.id === c.user_id || user.role === 'admin') && (
                    <button
                      className="btn-remove comment-delete"
                      onClick={() => handleDeleteComment(c.id)}
                      title="Eliminar comentario"
                    >✕</button>
                  )}
                </div>
                <p className="comment-content">{c.content}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {canEdit && (
        <div className="recipe-detail-actions">
          <Link to={`/recipes/${id}/edit`} className="btn btn-secondary">✏️ Editar</Link>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Eliminando…' : '🗑️ Eliminar'}
          </button>
        </div>
      )}

      <div className="recipe-detail-back">
        <Link to="/" className="btn btn-outline">← Volver a recetas</Link>
      </div>
    </div>
  );
}

export default RecipeDetail;
